import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import { AdventureConfig } from '@modules/adventure/config';

interface FallingItem {
  readonly sprite: Phaser.GameObjects.Text;
  readonly speed: number;
  readonly isHazard: boolean;
}

const PLAYER_EMOJI = '🌟';
const STAR_EMOJI = '⭐';
const HAZARD_EMOJI = '🌩️';
const ITEM_FONT_SIZE = '56px';
const PLAYER_FONT_SIZE = '64px';

/**
 * Minijuego de aventura: el jugador mueve su estrella en la parte inferior para
 * atrapar estrellas (suman puntos) y esquivar nubes de tormenta (restan vidas).
 * Usa control tactil (seguir el dedo) y teclado (flechas). Segundo ejemplo del
 * patron modular, con mecanica distinta a la del minijuego de ritmo.
 */
export class AdventureScene extends BaseScene {
  private score = 0;
  private lives: number = AdventureConfig.startingLives;
  private spawnIntervalMs: number = AdventureConfig.initialSpawnIntervalMs;
  private isPlaying = false;

  private player!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private items: FallingItem[] = [];
  private timeUntilSpawnMs = 0;

  public constructor() {
    super(AdventureConfig.key);
  }

  public create(): void {
    this.resetState();
    this.add.rectangle(this.centerX, this.centerY, this.scale.width, this.scale.height, Palette.background);

    this.player = this.add
      .text(this.centerX, this.scale.height - 90, PLAYER_EMOJI, { fontSize: PLAYER_FONT_SIZE })
      .setOrigin(0.5);

    this.scoreText = this.add.text(40, 30, '', {
      fontFamily: 'Trebuchet MS, sans-serif',
      fontSize: '34px',
      color: Palette.textLight,
    });

    this.livesText = this.add
      .text(this.scale.width - 40, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.setupInput();
    this.refreshHud();
    this.isPlaying = true;
    this.timeUntilSpawnMs = this.spawnIntervalMs;
  }

  public update(_time: number, deltaMs: number): void {
    if (!this.isPlaying) {
      return;
    }
    this.handleKeyboardMovement(deltaMs);
    this.handleSpawning(deltaMs);
    this.handleFalling(deltaMs);
  }

  private resetState(): void {
    this.score = 0;
    this.lives = AdventureConfig.startingLives;
    this.spawnIntervalMs = AdventureConfig.initialSpawnIntervalMs;
    this.isPlaying = false;
    this.items = [];
  }

  private setupInput(): void {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown || this.input.activePointer.isDown) {
        this.movePlayerTo(pointer.x);
      }
    });
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      this.movePlayerTo(pointer.x);
    });
  }

  private movePlayerTo(x: number): void {
    const margin = AdventureConfig.collisionRadius;
    this.player.x = Phaser.Math.Clamp(x, margin, this.scale.width - margin);
  }

  private handleKeyboardMovement(deltaMs: number): void {
    const distance = (AdventureConfig.playerSpeed * deltaMs) / 1000;
    if (this.cursors.left.isDown) {
      this.movePlayerTo(this.player.x - distance);
    } else if (this.cursors.right.isDown) {
      this.movePlayerTo(this.player.x + distance);
    }
  }

  private handleSpawning(deltaMs: number): void {
    this.timeUntilSpawnMs -= deltaMs;
    if (this.timeUntilSpawnMs > 0) {
      return;
    }
    this.timeUntilSpawnMs = this.spawnIntervalMs;
    this.spawnItem();
  }

  private spawnItem(): void {
    const margin = AdventureConfig.collisionRadius;
    const x = Phaser.Math.Between(margin, this.scale.width - margin);
    const isHazard = Math.random() < AdventureConfig.hazardChance;
    const speed = Phaser.Math.Between(AdventureConfig.fallSpeedMin, AdventureConfig.fallSpeedMax);

    const sprite = this.add
      .text(x, -40, isHazard ? HAZARD_EMOJI : STAR_EMOJI, { fontSize: ITEM_FONT_SIZE })
      .setOrigin(0.5);

    this.items.push({ sprite, speed, isHazard });
  }

  private handleFalling(deltaMs: number): void {
    const fallDistance = deltaMs / 1000;
    const survivors: FallingItem[] = [];

    for (const item of this.items) {
      item.sprite.y += item.speed * fallDistance;

      if (this.collidesWithPlayer(item.sprite)) {
        this.resolveCatch(item);
        continue;
      }

      if (item.sprite.y > this.scale.height + 60) {
        item.sprite.destroy();
        continue;
      }

      survivors.push(item);
    }

    this.items = survivors;
  }

  private collidesWithPlayer(sprite: Phaser.GameObjects.Text): boolean {
    const distance = Phaser.Math.Distance.Between(sprite.x, sprite.y, this.player.x, this.player.y);
    return distance < AdventureConfig.collisionRadius;
  }

  private resolveCatch(item: FallingItem): void {
    item.sprite.destroy();
    if (item.isHazard) {
      this.loseLife();
      return;
    }
    this.audio.playHit();
    this.score += AdventureConfig.pointsPerStar;
    this.spawnIntervalMs = Math.max(
      AdventureConfig.minSpawnIntervalMs,
      this.spawnIntervalMs - AdventureConfig.spawnSpeedupPerHitMs,
    );
    this.refreshHud();
  }

  private loseLife(): void {
    this.lives -= 1;
    this.audio.playWrong();
    this.cameras.main.shake(180, 0.01);
    this.refreshHud();
    if (this.lives <= 0) {
      this.endRound();
    }
  }

  private refreshHud(): void {
    this.scoreText.setText(`Puntos: ${this.score}`);
    this.livesText.setText(`Vidas: ${'❤️'.repeat(this.lives)}`);
  }

  private endRound(): void {
    this.isPlaying = false;
    this.items.forEach((item) => item.sprite.destroy());
    this.items = [];

    const isRecord = this.profile.submitScore(AdventureConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * AdventureConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.showResults(isRecord, coins);
  }

  private showResults(isRecord: boolean, coins: number): void {
    const overlay = this.add.rectangle(
      this.centerX,
      this.centerY,
      this.scale.width,
      this.scale.height,
      Palette.background,
      0.85,
    );
    overlay.setInteractive();

    this.add
      .text(this.centerX, this.centerY - 150, isRecord ? '¡Nuevo record!' : '¡Buen intento!', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '52px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, this.centerY - 60, `Puntos: ${this.score}\nEstrellas ganadas: ${coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    new TextButton(this, {
      x: this.centerX - 180,
      y: this.centerY + 80,
      label: 'Jugar de nuevo',
      color: Palette.secondary,
      onClick: () => this.scene.restart(),
    });

    new TextButton(this, {
      x: this.centerX + 180,
      y: this.centerY + 80,
      label: 'Volver al menu',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }
}
